/**
 * InlineVisuals — Compact visual components rendered inline within chat messages.
 *
 * 11 types optimized for contextual data display without requiring the artifact panel:
 *   1. kpi_cards     — Row of KPI metric cards
 *   2. mini_bar      — Compact horizontal bar chart (≤8 items)
 *   3. status_timeline — Horizontal step flow (Quote → Order → Job → Ship → Invoice)
 *   4. sparkline     — Tiny trend line (40px tall)
 *   5. gauge         — Circular progress/utilization meter
 *   6. progress_bar  — Horizontal progress with label
 *   7. comparison    — Side-by-side estimated vs actual bars
 *   8. donut         — Ring chart for proportional splits
 *   9. traffic_light — Status indicator (green/yellow/red)
 *  10. delta         — Change indicator with arrow and percentage
 *  11. countdown     — Time remaining with urgency coloring
 */

import { useMemo } from 'react'

// ── Color palette ──────────────────────────────────────────────────────────────
const COLORS = {
  green: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-700 dark:text-emerald-400', fill: '#10b981', border: 'border-emerald-200 dark:border-emerald-800' },
  yellow: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-700 dark:text-amber-400', fill: '#f59e0b', border: 'border-amber-200 dark:border-amber-800' },
  red: { bg: 'bg-red-50 dark:bg-red-950/30', text: 'text-red-700 dark:text-red-400', fill: '#ef4444', border: 'border-red-200 dark:border-red-800' },
  blue: { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-700 dark:text-blue-400', fill: '#3b82f6', border: 'border-blue-200 dark:border-blue-800' },
  purple: { bg: 'bg-purple-50 dark:bg-purple-950/30', text: 'text-purple-700 dark:text-purple-400', fill: '#8b5cf6', border: 'border-purple-200 dark:border-purple-800' },
  indigo: { bg: 'bg-indigo-50 dark:bg-indigo-950/30', text: 'text-indigo-700 dark:text-indigo-400', fill: '#6366f1', border: 'border-indigo-200 dark:border-indigo-800' },
}

const BAR_PALETTE = ['#6366f1', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#84cc16']

// ── Format helpers ─────────────────────────────────────────────────────────────
function fmtCurrency(v) {
  if (typeof v !== 'number') return v
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}K`
  return `$${v.toLocaleString()}`
}

function fmtPercent(v) {
  return typeof v === 'number' ? `${v.toFixed(1)}%` : v
}

function fmtNumber(v) {
  return typeof v === 'number' ? v.toLocaleString() : v
}

function fmtValue(v, format) {
  if (format === 'currency') return fmtCurrency(v)
  if (format === 'percent') return fmtPercent(v)
  return fmtNumber(v)
}

// ── 1. KPI Cards ───────────────────────────────────────────────────────────────
function KpiCards({ data }) {
  const items = data.items || []
  if (!items.length) return null

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 my-3 animate-in fade-in duration-500">
      {items.map((item, i) => {
        const statusColor = item.status === 'success' ? 'border-l-emerald-500' :
          item.status === 'warning' ? 'border-l-amber-500' :
          item.status === 'danger' ? 'border-l-red-500' : 'border-l-blue-500'
        return (
          <div key={i}
            className={`rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${statusColor} bg-white dark:bg-gray-800/50 p-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`}
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium mb-0.5">{item.label}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmtValue(item.value, item.format)}</p>
            {item.change && (
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`text-xs font-medium ${
                  item.change > 0 ? 'text-emerald-600 dark:text-emerald-400' :
                  item.change < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'
                }`}>
                  {item.change > 0 ? '▲' : item.change < 0 ? '▼' : '–'} {Math.abs(item.change)}{item.changeFormat === 'percent' ? '%' : ''}
                </span>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── 2. Mini Bar ────────────────────────────────────────────────────────────────
function MiniBar({ data }) {
  const items = data.items || data.data || []
  const labelKey = data.labelKey || 'label'
  const valueKey = data.valueKey || 'value'
  const format = data.format || 'currency'
  if (!items.length) return null

  const maxVal = Math.max(...items.map(d => Math.abs(d[valueKey] || 0)), 1)

  return (
    <div className="my-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden animate-in fade-in duration-500">
      {data.title && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">{data.title}</p>
        </div>
      )}
      <div className="px-4 py-3 space-y-2">
        {items.slice(0, 8).map((item, i) => {
          const val = item[valueKey] || 0
          const pct = Math.abs(val) / maxVal * 100
          const color = item.color || BAR_PALETTE[i % BAR_PALETTE.length]
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs text-gray-600 dark:text-gray-400 w-24 truncate flex-shrink-0 text-right">
                {item[labelKey]}
              </span>
              <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                    animationDelay: `${i * 60}ms`
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-16 text-right">
                {fmtValue(val, format)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 3. Status Timeline ─────────────────────────────────────────────────────────
function StatusTimeline({ data }) {
  const steps = data.steps || []
  if (!steps.length) return null

  return (
    <div className="my-3 animate-in fade-in duration-500">
      {data.title && <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">{data.title}</p>}
      <div className="flex items-center gap-0 overflow-x-auto py-1">
        {steps.map((step, i) => {
          const status = step.status || 'pending' // complete, active, pending, error
          const dotColor = status === 'complete' ? 'bg-emerald-500' :
            status === 'active' ? 'bg-blue-500 animate-pulse' :
            status === 'error' ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'
          const textColor = status === 'complete' ? 'text-emerald-700 dark:text-emerald-400' :
            status === 'active' ? 'text-blue-700 dark:text-blue-400 font-semibold' :
            status === 'error' ? 'text-red-700 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
          const lineColor = status === 'complete' ? 'bg-emerald-400' : 'bg-gray-200 dark:bg-gray-700'

          return (
            <div key={i} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center">
                <div className={`w-3 h-3 rounded-full ${dotColor} transition-all duration-300`}>
                  {status === 'complete' && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 12 12">
                      <path d="M3.5 6L5.5 8L8.5 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    </svg>
                  )}
                </div>
                <span className={`text-[10px] mt-1 whitespace-nowrap ${textColor}`}>{step.label}</span>
                {step.date && <span className="text-[9px] text-gray-400 dark:text-gray-500">{step.date}</span>}
              </div>
              {i < steps.length - 1 && (
                <div className={`w-8 sm:w-12 h-0.5 ${lineColor} mx-1 mt-[-12px]`} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 4. Sparkline ───────────────────────────────────────────────────────────────
function Sparkline({ data }) {
  const values = data.values || []
  const label = data.label || ''
  const current = data.current || values[values.length - 1]
  const format = data.format || 'currency'
  const color = data.color || '#6366f1'

  if (!values.length) return null

  const width = 200
  const height = 36
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1

  const points = values.map((v, i) => ({
    x: (i / (values.length - 1)) * width,
    y: height - ((v - min) / range) * (height - 4) - 2
  }))
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const areaD = pathD + ` L ${width} ${height} L 0 ${height} Z`

  // Trend
  const firstHalf = values.slice(0, Math.floor(values.length / 2))
  const secondHalf = values.slice(Math.floor(values.length / 2))
  const avg1 = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length
  const avg2 = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length
  const trending = avg2 > avg1 * 1.02 ? 'up' : avg2 < avg1 * 0.98 ? 'down' : 'flat'

  return (
    <div className="inline-flex items-center gap-3 my-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 animate-in fade-in duration-500">
      {label && <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>}
      <svg width={width} height={height} className="flex-shrink-0">
        <defs>
          <linearGradient id={`spark-grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.15} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#spark-grad-${color.replace('#', '')})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r={3} fill={color} />
      </svg>
      <div className="flex flex-col">
        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{fmtValue(current, format)}</span>
        <span className={`text-[10px] font-medium ${
          trending === 'up' ? 'text-emerald-600' : trending === 'down' ? 'text-red-600' : 'text-gray-400'
        }`}>
          {trending === 'up' ? '▲ Trending up' : trending === 'down' ? '▼ Trending down' : '– Stable'}
        </span>
      </div>
    </div>
  )
}

// ── 5. Gauge ───────────────────────────────────────────────────────────────────
function Gauge({ data }) {
  const value = data.value || 0
  const max = data.max || 100
  const label = data.label || ''
  const format = data.format || 'percent'
  const pct = Math.min(100, Math.max(0, (value / max) * 100))

  // Color based on thresholds
  const color = pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#10b981'
  const size = 100
  const strokeWidth = 10
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference * (1 - pct / 100)

  return (
    <div className="inline-flex flex-col items-center my-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 animate-in fade-in duration-500">
      {/* `relative` on the SVG wrapper scopes the absolutely-positioned
          value label to the gauge — without it the label escapes to the
          nearest positioned ancestor (often the page root) and renders
          way below the input area. */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke="#e2e8f0" strokeWidth={strokeWidth} className="dark:stroke-gray-700" />
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            className="transition-all duration-1000 ease-out" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmtValue(value, format)}</span>
        </div>
      </div>
      {label && <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 font-medium">{label}</span>}
    </div>
  )
}

// ── 6. Progress Bar ────────────────────────────────────────────────────────────
function ProgressBar({ data }) {
  const value = data.value || 0
  const max = data.max || 100
  const label = data.label || ''
  const detail = data.detail || ''
  const pct = Math.min(100, Math.max(0, (value / max) * 100))
  const color = pct >= 100 ? '#10b981' : pct > 75 ? '#3b82f6' : pct > 50 ? '#f59e0b' : '#ef4444'

  return (
    <div className="my-2 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 animate-in fade-in duration-500">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-xs font-bold text-gray-900 dark:text-gray-100">{pct.toFixed(0)}%</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      {detail && <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">{detail}</p>}
    </div>
  )
}

// ── 7. Comparison Pair ─────────────────────────────────────────────────────────
function Comparison({ data }) {
  const items = data.items || []
  if (!items.length) return null

  const maxVal = Math.max(...items.flatMap(i => [Math.abs(i.estimated || 0), Math.abs(i.actual || 0)]), 1)

  return (
    <div className="my-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden animate-in fade-in duration-500">
      {data.title && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">{data.title}</p>
        </div>
      )}
      <div className="px-4 py-3 space-y-3">
        <div className="flex items-center gap-4 text-[10px] text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500" /> {data.estimatedLabel || 'Estimated'}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-500" /> {data.actualLabel || 'Actual'}</span>
        </div>
        {items.map((item, i) => {
          const estPct = (Math.abs(item.estimated || 0) / maxVal) * 100
          const actPct = (Math.abs(item.actual || 0) / maxVal) * 100
          const overrun = item.actual > item.estimated
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600 dark:text-gray-400">{item.label}</span>
                {overrun && (
                  <span className="text-[10px] font-bold text-red-600 dark:text-red-400">
                    +{(((item.actual - item.estimated) / item.estimated) * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              <div className="space-y-1">
                <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-blue-500 transition-all duration-700" style={{ width: `${estPct}%` }} />
                </div>
                <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${overrun ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${actPct}%` }} />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 8. Donut ───────────────────────────────────────────────────────────────────
function Donut({ data }) {
  const segments = data.segments || []
  const centerLabel = data.centerLabel || ''
  const centerValue = data.centerValue || ''
  if (!segments.length) return null

  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0) || 1
  const size = 110
  const strokeWidth = 14
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  let accumulated = 0
  const arcs = segments.map((seg, i) => {
    const pct = seg.value / total
    const offset = circumference * (1 - pct)
    const rotation = (accumulated / total) * 360
    accumulated += seg.value
    return { ...seg, offset, rotation, color: seg.color || BAR_PALETTE[i % BAR_PALETTE.length] }
  })

  return (
    <div className="inline-flex items-center gap-4 my-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 animate-in fade-in duration-500">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          {arcs.map((arc, i) => (
            <circle key={i} cx={size / 2} cy={size / 2} r={radius} fill="none"
              stroke={arc.color} strokeWidth={strokeWidth} strokeLinecap="butt"
              strokeDasharray={circumference} strokeDashoffset={arc.offset}
              transform={`rotate(${arc.rotation - 90} ${size / 2} ${size / 2})`}
              className="transition-all duration-700 ease-out" />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{centerValue}</span>
          {centerLabel && <span className="text-[9px] text-gray-500 dark:text-gray-400">{centerLabel}</span>}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: arcs[i]?.color }} />
            <span className="text-[11px] text-gray-600 dark:text-gray-400">{seg.label}</span>
            <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200 ml-auto">
              {fmtValue(seg.value, data.format || 'currency')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── 9. Traffic Light ───────────────────────────────────────────────────────────
function TrafficLight({ data }) {
  const status = data.status || 'neutral' // good, warning, critical, neutral
  const label = data.label || ''
  const detail = data.detail || ''

  const config = {
    good: { color: 'bg-emerald-500', glow: 'shadow-emerald-500/30', text: 'text-emerald-700 dark:text-emerald-400', icon: '✓', bgClass: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' },
    warning: { color: 'bg-amber-500', glow: 'shadow-amber-500/30', text: 'text-amber-700 dark:text-amber-400', icon: '⚠', bgClass: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800' },
    critical: { color: 'bg-red-500', glow: 'shadow-red-500/30', text: 'text-red-700 dark:text-red-400', icon: '✕', bgClass: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800' },
    neutral: { color: 'bg-gray-400', glow: '', text: 'text-gray-600 dark:text-gray-400', icon: '–', bgClass: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700' },
  }

  const c = config[status] || config.neutral

  return (
    <div className={`inline-flex items-center gap-2.5 my-2 px-3 py-2 rounded-lg border ${c.bgClass} animate-in fade-in duration-500`}>
      <div className={`w-4 h-4 rounded-full ${c.color} shadow-lg ${c.glow} flex items-center justify-center`}>
        <span className="text-white text-[9px] font-bold">{c.icon}</span>
      </div>
      <div>
        <span className={`text-xs font-semibold ${c.text}`}>{label}</span>
        {detail && <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-2">{detail}</span>}
      </div>
    </div>
  )
}

// ── 10. Delta Indicator ────────────────────────────────────────────────────────
function Delta({ data }) {
  const value = data.value || 0
  const label = data.label || ''
  const format = data.format || 'percent'
  const context = data.context || ''
  const positive = value > 0
  const negative = value < 0

  const arrowColor = positive ? 'text-emerald-600 dark:text-emerald-400' :
    negative ? 'text-red-600 dark:text-red-400' : 'text-gray-400'
  const bgColor = positive ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' :
    negative ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800' :
    'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'

  return (
    <div className={`inline-flex items-center gap-2 my-2 px-3 py-2 rounded-lg border ${bgColor} animate-in fade-in duration-500`}>
      <span className={`text-lg font-bold ${arrowColor}`}>
        {positive ? '▲' : negative ? '▼' : '–'}
      </span>
      <div>
        <span className={`text-sm font-bold ${arrowColor}`}>
          {positive ? '+' : ''}{fmtValue(value, format)}
        </span>
        {label && <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">{label}</span>}
        {context && <p className="text-[10px] text-gray-500 dark:text-gray-400">{context}</p>}
      </div>
    </div>
  )
}

// ── 11. Countdown ──────────────────────────────────────────────────────────────
function Countdown({ data }) {
  const days = data.days ?? 0
  const label = data.label || ''
  const detail = data.detail || ''

  // Urgency coloring
  const urgency = days <= 0 ? 'overdue' : days <= 3 ? 'urgent' : days <= 7 ? 'soon' : 'normal'
  const config = {
    overdue: { bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-300 dark:border-red-800', text: 'text-red-700 dark:text-red-400', badge: 'bg-red-500', emoji: '🔴' },
    urgent: { bg: 'bg-orange-50 dark:bg-orange-950/30', border: 'border-orange-300 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-400', badge: 'bg-orange-500', emoji: '🟠' },
    soon: { bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-300 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-400', badge: 'bg-amber-500', emoji: '🟡' },
    normal: { bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-300 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-400', badge: 'bg-blue-500', emoji: '🔵' },
  }
  const c = config[urgency]

  return (
    <div className={`inline-flex items-center gap-2.5 my-2 px-3 py-2 rounded-lg border ${c.border} ${c.bg} animate-in fade-in duration-500`}>
      <div className={`w-7 h-7 rounded-full ${c.badge} flex items-center justify-center`}>
        <span className="text-white text-xs font-bold">{days <= 0 ? '!' : days}</span>
      </div>
      <div>
        <span className={`text-xs font-semibold ${c.text}`}>
          {days <= 0 ? 'OVERDUE' : `${days} day${days !== 1 ? 's' : ''} remaining`}
        </span>
        {label && <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-1.5">— {label}</span>}
        {detail && <p className="text-[10px] text-gray-500 dark:text-gray-400">{detail}</p>}
      </div>
    </div>
  )
}

// ── Stacked Segment Bar (bonus) ────────────────────────────────────────────────
function SegmentBar({ data }) {
  const segments = data.segments || []
  const title = data.title || ''
  if (!segments.length) return null

  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0) || 1

  return (
    <div className="my-2 animate-in fade-in duration-500">
      {title && <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{title}</p>}
      <div className="w-full h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
        {segments.map((seg, i) => {
          const pct = (seg.value / total) * 100
          const color = seg.color || BAR_PALETTE[i % BAR_PALETTE.length]
          return (
            <div key={i} className="h-full transition-all duration-700 relative group"
              style={{ width: `${pct}%`, backgroundColor: color }}
              title={`${seg.label}: ${fmtValue(seg.value, data.format || 'currency')} (${pct.toFixed(0)}%)`}
            />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
        {segments.map((seg, i) => (
          <span key={i} className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
            <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: seg.color || BAR_PALETTE[i % BAR_PALETTE.length] }} />
            {seg.label}: {fmtValue(seg.value, data.format || 'currency')}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Heatmap Row (bonus) ────────────────────────────────────────────────────────
function HeatmapRow({ data }) {
  const cells = data.cells || []
  const title = data.title || ''
  if (!cells.length) return null

  const maxVal = Math.max(...cells.map(c => c.value || 0), 1)

  return (
    <div className="my-2 animate-in fade-in duration-500">
      {title && <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{title}</p>}
      <div className="flex gap-1">
        {cells.map((cell, i) => {
          const intensity = (cell.value || 0) / maxVal
          const bg = intensity > 0.9 ? '#ef4444' : intensity > 0.7 ? '#f59e0b' : intensity > 0.4 ? '#3b82f6' : '#10b981'
          return (
            <div key={i} className="flex flex-col items-center">
              <div className="w-8 h-8 rounded-md flex items-center justify-center text-white text-[10px] font-bold transition-all duration-300"
                style={{ backgroundColor: bg, opacity: 0.3 + intensity * 0.7 }}
                title={`${cell.label}: ${cell.value}`}
              >
                {typeof cell.value === 'number' ? cell.value : ''}
              </div>
              <span className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5">{cell.label}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Annotation Callout (bonus) ─────────────────────────────────────────────────
function Callout({ data }) {
  const type = data.type || 'info' // info, warning, success, error
  const message = data.message || ''
  const config = {
    info: { bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-800 dark:text-blue-300', icon: 'ℹ' },
    warning: { bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-800 dark:text-amber-300', icon: '⚠' },
    success: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-800 dark:text-emerald-300', icon: '✓' },
    error: { bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-200 dark:border-red-800', text: 'text-red-800 dark:text-red-300', icon: '✕' },
  }
  const c = config[type] || config.info

  return (
    <div className={`flex items-start gap-2 my-2 px-3 py-2.5 rounded-lg border ${c.border} ${c.bg} animate-in fade-in duration-500`}>
      <span className={`text-sm ${c.text}`}>{c.icon}</span>
      <p className={`text-xs ${c.text}`}>{message}</p>
    </div>
  )
}

// ── Mini Table (bonus) ─────────────────────────────────────────────────────────
function MiniTable({ data }) {
  const rows = data.rows || []
  const title = data.title || ''
  if (!rows.length) return null

  return (
    <div className="my-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden animate-in fade-in duration-500">
      {title && (
        <div className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</p>
        </div>
      )}
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {rows.slice(0, 5).map((row, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="flex items-center gap-2">
              {row.rank && <span className="text-[10px] font-bold text-gray-400 w-4">{row.rank}</span>}
              <span className="text-xs text-gray-700 dark:text-gray-300">{row.label}</span>
            </div>
            <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{fmtValue(row.value, data.format || 'currency')}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Master Renderer ────────────────────────────────────────────────────────────

/**
 * Try to parse inline visual JSON from a string.
 * Returns the parsed object if it matches {"type": "inline", ...}, else null.
 */
export function tryParseInlineVisual(text) {
  if (!text) return null
  const str = String(text).trim()
  if (!str.startsWith('{')) return null
  try {
    const parsed = JSON.parse(str)
    if (parsed && parsed.type === 'inline' && parsed.vizType) {
      return parsed
    }
  } catch {
    // not valid JSON
  }
  return null
}

/**
 * Parse content for inline visual blocks embedded in code fences.
 * Returns array of { visual, beforeText, afterText } segments.
 */
export function parseInlineVisuals(content) {
  if (!content) return null
  const codeBlockRegex = /```(?:json)?\s*\n?([\s\S]*?)\n?```/g
  let match
  const visuals = []
  let lastIndex = 0

  while ((match = codeBlockRegex.exec(content)) !== null) {
    const visual = tryParseInlineVisual(match[1])
    if (visual) {
      const beforeText = content.substring(lastIndex, match.index).trim()
      visuals.push({ type: 'text', content: beforeText })
      visuals.push({ type: 'visual', data: visual })
      lastIndex = match.index + match[0].length
    }
  }

  if (visuals.length === 0) return null

  const afterText = content.substring(lastIndex).trim()
  if (afterText) visuals.push({ type: 'text', content: afterText })

  return visuals
}

/**
 * InlineVisual — renders a single inline visual based on vizType
 */
export default function InlineVisual({ data }) {
  if (!data || !data.vizType) return null

  return (
    <div data-viz="true" aria-hidden="false">
      {(() => {
        switch (data.vizType) {
          case 'kpi_cards': return <KpiCards data={data} />
          case 'mini_bar': return <MiniBar data={data} />
          case 'status_timeline': return <StatusTimeline data={data} />
          case 'sparkline': return <Sparkline data={data} />
          case 'gauge': return <Gauge data={data} />
          case 'progress_bar': return <ProgressBar data={data} />
          case 'comparison': return <Comparison data={data} />
          case 'donut': return <Donut data={data} />
          case 'traffic_light': return <TrafficLight data={data} />
          case 'delta': return <Delta data={data} />
          case 'countdown': return <Countdown data={data} />
          case 'segment_bar': return <SegmentBar data={data} />
          case 'heatmap_row': return <HeatmapRow data={data} />
          case 'callout': return <Callout data={data} />
          case 'mini_table': return <MiniTable data={data} />
          default: return null
        }
      })()}
    </div>
  )
}
